"use client"

import { useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { motion } from "framer-motion"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { ChevronRight, Layers } from "lucide-react"
import ProductShowcase from "./3d/product-showcase"

interface Category {
  id: number
  name: string
  image: string
  meta: {
    title: string
    description: string
    keywords: string
  }
  subcategories?: Array<{
    id: string
    name: string
    meta: {
      title: string
      description: string
      keywords: string
    }
  }>
}

interface ModernCategoryCardProps {
  category: Category
  index?: number
}

export default function ModernCategoryCard({ category, index = 0 }: ModernCategoryCardProps) {
  const [isHovered, setIsHovered] = useState(false)
  const [show3D, setShow3D] = useState(false)

  // Add safety checks
  if (!category || !category.meta) {
    return null
  }

  const slug = category.name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      whileHover={{ scale: 1.02, y: -5 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      <Card className="group relative overflow-hidden bg-white/80 backdrop-blur-sm border-0 shadow-lg hover:shadow-2xl transition-all duration-500 h-full">
        {/* Animated background gradient */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-purple-500/5 to-cyan-500/10"
          animate={{
            background: isHovered
              ? "linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(147, 51, 234, 0.1), rgba(6, 182, 212, 0.15))"
              : "linear-gradient(135deg, rgba(59, 130, 246, 0.05), rgba(147, 51, 234, 0.02), rgba(6, 182, 212, 0.05))",
          }}
          transition={{ duration: 0.5 }}
        />

        <CardContent className="p-0 relative">
          <Link href={`/categories/${slug}`}>
            <div className="relative aspect-[4/3] overflow-hidden" aria-label={`View details for ${category.name}`}>
              {show3D ? (
                <ProductShowcase productType={category.name} />
              ) : (
                <div className="relative w-full h-full">
                  <Image
                    src={`${category.image}`}
                    alt={category.name}
                    fill
                    className="object-cover transition-transform duration-700 group-hover:scale-110"
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-slate-900/20 to-transparent" />
                </div>
              )}

              {/* 3D Toggle */}
              {/* <motion.button
                className="absolute top-3 right-3 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300"
                onClick={(e) => {
                  e.preventDefault()
                  setShow3D(!show3D)
                }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <Layers className="h-4 w-4 text-slate-700" />
              </motion.button> */}

              {/* Category info overlay */}
              <div className="absolute bottom-4 left-4 right-4">
                <motion.h3
                  className="font-bold text-xl text-white mb-2 group-hover:text-slate-200 transition-colors"
                  whileHover={{ x: 5 }}
                >
                  {category.name}
                </motion.h3>
                {category.subcategories && category.subcategories.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    <Badge className="bg-white/20 text-white border-white/30 backdrop-blur-sm">
                      {category.subcategories.length} subcategories
                    </Badge>
                  </motion.div>
                )}
              </div>
            </div>

            <div className="p-6">
              <p className="text-slate-600 text-sm line-clamp-2 mb-4 leading-relaxed">
                {category.meta?.description || "No description available"}
              </p>

              <motion.div className="flex items-center justify-between" whileHover={{ x: 5 }}>
                <div className="flex items-center text-slate-900 font-medium group-hover:text-slate-600 transition-colors">
                  <span>Explore Products</span>
                  <motion.div animate={{ x: isHovered ? 5 : 0 }} transition={{ duration: 0.3 }}>
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </motion.div>
                </div>

                {category.subcategories && category.subcategories.length > 0 && (
                  <Badge variant="outline" className="text-xs">
                    {category.subcategories.length} types
                  </Badge>
                )}
              </motion.div>
            </div>
          </Link>
        </CardContent>
      </Card>
    </motion.div>
  )
}
